import { create } from "zustand";
import type { ProblemCounts } from "@/editor/diagnostics";

export type SidePanel =
  | "files"
  | "search"
  | "git"
  | "run"
  | "extensions"
  | "claude"
  | "people"
  | "chat"
  | "suggestions"
  | "improvements"
  | "history"
  | "sessions"
  /** Ferramenta de uma extensão do VS Code (GitLens, Docker…): "tool:<id>". */
  | `tool:${string}`;

/** Abas do painel inferior. */
export type BottomTab = "terminal" | "problems" | "output" | "comments";

/** Grupo de editores (o editor dividido tem dois, lado a lado). */
export interface EditorGroup {
  tabs: string[];
  active: string | null;
}

export const MAX_GROUPS = 2;

function readBool(key: string, fallback: boolean) {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === "1";
  } catch {
    return fallback;
  }
}
function writeBool(key: string, value: boolean) {
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // sem armazenamento: vale só nesta aba
  }
}

const TOOLS_KEY = "kompil.activity.hidden";
function readList(key: string): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").slice(0, 40) : [];
  } catch {
    return [];
  }
}
function writeList(key: string, value: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // sem armazenamento: vale só nesta aba
  }
}

const SIDE_PANELS: readonly SidePanel[] = [
  "files",
  "search",
  "git",
  "run",
  "extensions",
  "suggestions",
  "improvements",
  "history",
  "sessions",
];
const BOTTOM_TABS: readonly BottomTab[] = ["terminal", "problems", "output", "comments"];

/** Layout lembrado entre as sessões (os tamanhos ficam com os próprios painéis). */
interface SavedLayout {
  panel: SidePanel | null;
  terminalOpen: boolean;
  bottomTab: BottomTab;
}
const LAYOUT_KEY = "kompil.layout.state";
export const DEFAULT_LAYOUT: SavedLayout = { panel: "files", terminalOpen: true, bottomTab: "terminal" };

function readLayout(): SavedLayout {
  try {
    const raw = JSON.parse(localStorage.getItem(LAYOUT_KEY) ?? "null") as Partial<SavedLayout> | null;
    if (!raw) return DEFAULT_LAYOUT;
    return {
      panel: raw.panel === null || SIDE_PANELS.includes(raw.panel!) ? (raw.panel ?? null) : "files",
      terminalOpen: typeof raw.terminalOpen === "boolean" ? raw.terminalOpen : true,
      bottomTab: BOTTOM_TABS.includes(raw.bottomTab!) ? raw.bottomTab! : "terminal",
    };
  } catch {
    return DEFAULT_LAYOUT;
  }
}

/** Espelha o grupo em foco em `tabs`/`active` (o resto da interface lê só o grupo em foco). */
function withGroups(groups: EditorGroup[], focused: number) {
  const f = Math.min(focused, groups.length - 1);
  return { groups, focused: f, tabs: groups[f]!.tabs, active: groups[f]!.active };
}

/** Tira grupos vazios (sempre fica pelo menos um). */
function compact(groups: EditorGroup[], focused: number) {
  const kept = groups.filter((g) => g.tabs.length > 0);
  if (kept.length === groups.length) return withGroups(groups, focused);
  if (!kept.length) return withGroups([{ tabs: [], active: null }], 0);
  const focusedGroup = groups[focused];
  const idx = focusedGroup ? kept.indexOf(focusedGroup) : -1;
  return withGroups(kept, idx >= 0 ? idx : 0);
}

function closeIn(g: EditorGroup, id: string): EditorGroup {
  const idx = g.tabs.indexOf(id);
  if (idx < 0) return g;
  const tabs = g.tabs.filter((t) => t !== id);
  return { tabs, active: g.active === id ? (tabs[Math.max(0, idx - 1)] ?? null) : g.active };
}

interface WorkspaceState {
  groups: EditorGroup[];
  focused: number;
  /** Abas e arquivo ativo do grupo em foco. */
  tabs: string[];
  active: string | null;
  panel: SidePanel | null;
  terminalOpen: boolean;
  bottomTab: BottomTab;
  cursor: { line: number; col: number; selected: number } | null;
  conflict: { scope: string; users: Array<{ name: string; color: string }> } | null;
  /** Paleta aberta com este texto inicial ("" arquivos, ">" comandos, "@" símbolos, ":" linha). */
  palette: string | null;
  shareOpen: boolean;
  /** Aba do Compartilhar ao abrir (pelo nome, link ou membros). */
  shareTab: "name" | "invite" | "members" | null;
  settingsOpen: boolean;
  /** Modo zen (plugin kompil.zen-mode): só o editor na tela. */
  zen: boolean;
  /** Muda ao restaurar o layout padrão: os painéis remontam com os tamanhos padrão. */
  layoutVersion: number;
  /** Preferências pessoais (por navegador). */
  autocomplete: boolean;
  stuckDetection: boolean;
  /** Sublinhar erros e avisos no código. */
  diagnostics: boolean;
  /** Revisão da IA em segundo plano (sugestões de melhoria). */
  backgroundReview: boolean;
  /** Aviso quando alguém entra ou sai do projeto. */
  presenceNotices: boolean;
  /** Minimapa à direita do editor (com a posição dos colegas). */
  minimap: boolean;
  /** Ferramentas que a pessoa tirou da barra lateral (continuam nos atalhos e na paleta). */
  hiddenTools: string[];
  /** Barra lateral recolhida: só a ferramenta aberta e o botão com todas. */
  activityCollapsed: boolean;
  /** Problemas do arquivo aberto (null: nenhum editor aberto). */
  problems: ProblemCounts | null;
  /** "Novo arquivo/Nova pasta" pelo menu do app: a árvore de arquivos abre o campo de nome. */
  createRequest: { type: "file" | "folder"; at: number } | null;
  /** Pedido para abrir a conversa de comentário de uma linha (editor em foco). */
  commentRequest: { fileId: string; line: number; at: number } | null;

  openFile(id: string, group?: number): void;
  closeFile(id: string, group?: number): void;
  closeOthers(keep: string | null, group?: number): void;
  closeMissing(existing: Set<string>): void;
  moveTab(id: string, from: number, to: number, index: number): void;
  splitEditor(): void;
  focusGroup(group: number): void;
  cycleTab(delta: 1 | -1): void;
  setPanel(p: SidePanel | null): void;
  togglePanel(p: SidePanel): void;
  setTerminal(open: boolean): void;
  setBottomTab(tab: BottomTab): void;
  setCursor(c: WorkspaceState["cursor"]): void;
  setConflict(c: WorkspaceState["conflict"]): void;
  setPalette(open: boolean | string): void;
  setShare(open: boolean, tab?: "name" | "invite" | "members"): void;
  setSettings(open: boolean): void;
  setZen(zen: boolean): void;
  setAutocomplete(v: boolean): void;
  setStuckDetection(v: boolean): void;
  setDiagnostics(v: boolean): void;
  setBackgroundReview(v: boolean): void;
  setPresenceNotices(v: boolean): void;
  setMinimap(v: boolean): void;
  toggleTool(id: string): void;
  showAllTools(): void;
  setActivityCollapsed(v: boolean): void;
  setProblems(p: ProblemCounts | null): void;
  requestCreate(type: "file" | "folder"): void;
  requestComment(fileId: string, line: number): void;
  /** Projeto novo: fecha os editores e limpa o estado do projeto (o layout continua). */
  reset(): void;
}

const projectInitial = {
  ...withGroups([{ tabs: [], active: null }], 0),
  cursor: null,
  conflict: null,
  palette: null,
  shareOpen: false,
  shareTab: null as "name" | "invite" | "members" | null,
  settingsOpen: false,
  zen: false,
  problems: null as ProblemCounts | null,
  createRequest: null as { type: "file" | "folder"; at: number } | null,
  commentRequest: null as { fileId: string; line: number; at: number } | null,
};

export const useWorkspace = create<WorkspaceState>((set, get) => ({
  ...projectInitial,
  ...readLayout(),
  layoutVersion: 0,
  autocomplete: readBool("kompil.ai.autocomplete", false),
  stuckDetection: readBool("kompil.stuckDetection", false),
  diagnostics: readBool("kompil.editor.diagnostics", true),
  backgroundReview: readBool("kompil.ai.backgroundReview", true),
  presenceNotices: readBool("kompil.presenceNotices", true),
  minimap: readBool("kompil.editor.minimap", true),
  hiddenTools: readList(TOOLS_KEY),
  activityCollapsed: readBool("kompil.activity.collapsed", false),
  openFile: (id, group) =>
    set((s) => {
      const gi = group ?? s.focused;
      const groups = s.groups.map((g, i) => {
        if (i !== gi) return g;
        if (g.tabs.includes(id)) return { ...g, active: id };
        // Abre à direita da aba ativa, como no VS Code.
        const at = g.active ? g.tabs.indexOf(g.active) + 1 : g.tabs.length;
        return { tabs: [...g.tabs.slice(0, at), id, ...g.tabs.slice(at)], active: id };
      });
      return withGroups(groups, gi);
    }),
  closeFile: (id, group) =>
    set((s) => {
      const gi = group ?? s.focused;
      return compact(
        s.groups.map((g, i) => (i === gi ? closeIn(g, id) : g)),
        s.focused,
      );
    }),
  closeOthers: (keep, group) =>
    set((s) => {
      const gi = group ?? s.focused;
      return compact(
        s.groups.map((g, i) =>
          i === gi ? { tabs: keep && g.tabs.includes(keep) ? [keep] : [], active: keep } : g,
        ),
        s.focused,
      );
    }),
  closeMissing: (existing) => {
    const { groups, focused } = get();
    if (groups.every((g) => g.tabs.every((t) => existing.has(t)))) return;
    const next = groups.map((g) => {
      const tabs = g.tabs.filter((t) => existing.has(t));
      return { tabs, active: g.active && existing.has(g.active) ? g.active : (tabs[0] ?? null) };
    });
    set(compact(next, focused));
  },
  moveTab: (id, from, to, index) =>
    set((s) => {
      if (!s.groups[from] || to < 0 || to >= MAX_GROUPS) return {};
      let groups = s.groups.map((g) => ({ ...g, tabs: [...g.tabs] }));
      if (to >= groups.length) groups = [...groups, { tabs: [], active: null }];
      const src = groups[from]!;
      const dst = groups[to]!;
      const oldIdx = src.tabs.indexOf(id);
      if (oldIdx < 0) return {};
      if (from === to) {
        src.tabs.splice(oldIdx, 1);
        const at = Math.max(0, Math.min(index > oldIdx ? index - 1 : index, src.tabs.length));
        src.tabs.splice(at, 0, id);
        src.active = id;
      } else {
        groups[from] = closeIn(src, id);
        const tabs = dst.tabs.filter((t) => t !== id);
        tabs.splice(Math.max(0, Math.min(index, tabs.length)), 0, id);
        groups[to] = { tabs, active: id };
      }
      return compact(groups, to);
    }),
  splitEditor: () =>
    set((s) => {
      const active = s.active;
      if (!active) return {};
      if (s.groups.length < MAX_GROUPS) {
        return withGroups([...s.groups, { tabs: [active], active }], s.groups.length);
      }
      // Já dividido: abre o arquivo no outro grupo e passa o foco para lá.
      const other = s.focused === 0 ? 1 : 0;
      const groups = s.groups.map((g, i) =>
        i === other ? { tabs: g.tabs.includes(active) ? g.tabs : [...g.tabs, active], active } : g,
      );
      return withGroups(groups, other);
    }),
  focusGroup: (group) =>
    set((s) => (s.groups[group] && s.focused !== group ? withGroups(s.groups, group) : {})),
  cycleTab: (delta) =>
    set((s) => {
      const g = s.groups[s.focused]!;
      if (g.tabs.length < 2 || !g.active) return {};
      const idx = g.tabs.indexOf(g.active);
      const next = g.tabs[(idx + delta + g.tabs.length) % g.tabs.length]!;
      return withGroups(
        s.groups.map((x, i) => (i === s.focused ? { ...x, active: next } : x)),
        s.focused,
      );
    }),
  setPanel: (panel) => set({ panel }),
  togglePanel: (p) => set((s) => ({ panel: s.panel === p ? null : p })),
  setTerminal: (terminalOpen) => set({ terminalOpen }),
  setBottomTab: (bottomTab) => set({ bottomTab, terminalOpen: true }),
  setCursor: (cursor) => set({ cursor }),
  setConflict: (conflict) => set({ conflict }),
  setPalette: (open) => set({ palette: open === true ? "" : open === false ? null : open }),
  setShare: (shareOpen, tab) => set({ shareOpen, shareTab: tab ?? null }),
  setSettings: (settingsOpen) => set({ settingsOpen }),
  setZen: (zen) => set({ zen }),
  setAutocomplete: (v) => {
    writeBool("kompil.ai.autocomplete", v);
    set({ autocomplete: v });
  },
  setStuckDetection: (v) => {
    writeBool("kompil.stuckDetection", v);
    set({ stuckDetection: v });
  },
  setDiagnostics: (v) => {
    writeBool("kompil.editor.diagnostics", v);
    set({ diagnostics: v });
  },
  setBackgroundReview: (v) => {
    writeBool("kompil.ai.backgroundReview", v);
    set({ backgroundReview: v });
  },
  setMinimap: (v) => {
    writeBool("kompil.editor.minimap", v);
    set({ minimap: v });
  },
  toggleTool: (id) => {
    const hidden = get().hiddenTools;
    const next = hidden.includes(id) ? hidden.filter((t) => t !== id) : [...hidden, id];
    writeList(TOOLS_KEY, next);
    set({ hiddenTools: next });
  },
  showAllTools: () => {
    writeList(TOOLS_KEY, []);
    set({ hiddenTools: [] });
  },
  setActivityCollapsed: (v) => {
    writeBool("kompil.activity.collapsed", v);
    set({ activityCollapsed: v });
  },
  setPresenceNotices: (v) => {
    writeBool("kompil.presenceNotices", v);
    set({ presenceNotices: v });
  },
  setProblems: (problems) => set({ problems }),
  requestCreate: (type) => set({ panel: "files", zen: false, createRequest: { type, at: Date.now() } }),
  requestComment: (fileId, line) => set({ commentRequest: { fileId, line, at: Date.now() } }),
  reset: () => set(projectInitial),
}));

// O layout (painel lateral, painel inferior e a aba dele) volta como estava na próxima abertura.
let saved = JSON.stringify(readLayout());
useWorkspace.subscribe((s) => {
  const next = JSON.stringify({ panel: s.panel, terminalOpen: s.terminalOpen, bottomTab: s.bottomTab });
  if (next === saved) return;
  saved = next;
  try {
    localStorage.setItem(LAYOUT_KEY, next);
  } catch {
    // sem armazenamento: o layout vale só nesta sessão
  }
});
